/**
 * Translation provider + ``useT()`` hook.
 *
 * Two languages today: ``en`` (default) and ``uk``. Only chrome
 * strings are translated — note bodies, recipe steps, etc. stay
 * in whatever language the user typed.
 *
 * Persistence: localStorage key ``vuzlyk.lang``. Cross-tab sync
 * via the ``storage`` event (mirrors AuthProvider / ThemeProvider).
 *
 * The legacy ``t`` object export (``t.signIn`` etc.) keeps working
 * during the design rebuild — it returns the English dictionary.
 * New code should use ``const [, T] = useT(); T("signIn")`` so the
 * Ukrainian variant can take effect.
 *
 * ``T(key, vars?)`` supports ``{name}`` placeholders inside the
 * looked-up string, replaced from ``vars``. ``Tn(key, count, vars?)``
 * picks a plural variant via ``Intl.PluralRules`` — looks up
 * ``${key}.${rule}`` (one/few/many/other) with ``other`` as the
 * default. Both helpers are returned from ``useT()``.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

export type LangId = "en" | "uk";

export const LANGS: readonly { id: LangId; name: string }[] = [
  { id: "en", name: "English" },
  { id: "uk", name: "Українська" },
];

const STORAGE_KEY = "vuzlyk.lang";
const DEFAULT_LANG: LangId = "uk";

// ---- Dictionary ---------------------------------------------------------
//
// English is canonical: missing keys in other languages fall back to it.
// Ukrainian translations marked ``// REVIEW`` need a native check before
// public release.
//
// EN_STRINGS is declared as a ``const`` object literal so each key gets
// its precise string type (``t.signIn`` is ``"Sign in"``, not
// ``string | undefined``). The other-language dicts are partials; missing
// keys fall back to English at lookup time.

const EN_STRINGS = {
    // ---- App chrome ----
    appTitle: "Vuzlyk",
    appTagline: "day-to-day, threaded together",

    // ---- App ids (used by sidebar / tabbar / breadcrumbs) ----
    today: "Today",
    tasks: "Tasks",
    notes: "Notes",
    cal: "Calendar",
    habit: "Habits",
    shop: "Shopping",
    cook: "Recipes",
    settings: "Settings",
    people: "People",
    "nav.apps": "Apps",
    "nav.system": "System",

    // ---- Shell chrome ----
    "shell.openMenu": "Open navigation menu",

    // ---- Auth ----
    signIn: "Sign in",
    signUp: "Sign up",
    signOut: "Sign out",
    email: "Email",
    password: "Password",
    noAccountYet: "No account yet?",
    alreadyHaveAccount: "Already have an account?",
    forgotPassword: "Forgot password?",
    resetPassword: "Reset password",
    resetEmailSent:
      "If that email exists in our system, a reset link is on its way — please check your inbox.",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    passwordsDoNotMatch: "Passwords do not match.",
    resetLinkInvalid: "This password reset link is expired or invalid.",
    requestNewResetLink: "Request new link",
    passwordResetSuccess:
      "Password updated. Please sign in with your new password.",
    backToLogin: "Back to login",
    finishingSignIn: "Finishing sign-in…",
    signInWithGoogle: "Sign in with Google",
    "auth.passwordHint": "12 characters minimum.",
    "auth.signInError": "Sign-in error",

    // ---- Google integration ----
    connectGoogle: "Connect Google",
    disconnectGoogle: "Disconnect",
    googleConnectedAs: "Connected as",
    googleNotConnected: "Not connected",
    googleAccount: "Google account",
    googleErrorCancelled: "Google sign-in was cancelled.",
    googleErrorAlreadyLinked:
      "That Google account is already linked to another user.",
    googleErrorEmailUnverified:
      "Sign in with your password and connect Google from settings.",
    googleErrorProvider: "Could not reach Google. Please try again.",
    googleErrorIdToken: "Google's response was invalid. Please try again.",
    googleErrorState: "The sign-in attempt expired. Please try again.",
    googleErrorExchange:
      "The sign-in finishing step expired. Please try again.",
    googleErrorGeneric: "Google sign-in failed.",

    // ---- Notes ----
    notesEmpty: "No notes yet — capture your first one below.",
    newNotePlaceholder: "Capture a thought…",
    tagsPlaceholder: "comma,separated,tags",
    "notes.allNotes": "All notes",
    "notes.sendViaTelegram": "Send via Telegram",
    "notes.contentLabel": "Note content",
    "notes.tagsLabel": "Tags",
    "notes.searchPlaceholder": "Search notes…",
    "notes.searchLabel": "Search notes",
    "notes.newNote": "New note",
    "notes.untitledDefault": "Untitled\n\n",
    "notes.emptyTitle": "(empty)",
    "notes.breadcrumbRoot": "notes",
    "notes.titleLabel": "title",
    "notes.titlePlaceholder": "Note title…",
    "notes.bodyLabel": "body · markdown",
    "notes.metaEdited": "edited {date}",
    "notes.metaCreatedAt": "created {date}",
    "notes.metaUpdatedAt": "last edited {date}",
    "notes.metaChars": "{count} chars",
    "notes.deleteConfirm": "Delete this note?",
    "notes.colorLabel": "color",
    "notes.colorAuto": "auto",
    "links.label": "linked",
    "links.add": "link",
    "links.remove": "remove link",
    "links.searchPlaceholder": "search notes, tasks, events, habits…",
    "links.noMatches": "no matches",
    "links.kind.note": "note",
    "links.kind.task": "task",
    "links.kind.task_list": "list",
    "links.kind.event": "event",
    "links.kind.habit": "habit",
    "links.kind.recipe": "recipe",
    "links.kind.shopping_list": "shop",
    "links.kind.shopping_item": "item",
    "links.kind.tag": "tag",

    // ---- Common verbs ----
    create: "Create",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    open: "open",
    loadMore: "Load more",
    loading: "Loading…",
    somethingWentWrong: "Something went wrong",
    comingSoon: "coming soon",
    "topbar.searchPlaceholder": "Search…",
    "common.soon": "soon",
    "common.open": "Open",
    "common.close": "Close",
    "edit.task.title": "edit task",
    "edit.event.title": "edit event",
    "edit.habit.title": "edit habit",
    "new.habit.title": "new habit",
    "edit.note.title": "edit note",
    "edit.shopItem.title": "edit item",
    "edit.taskList.title": "edit list",
    "today.cardOpen": "open",

    // ---- Today hub ----
    "today.greet.morning": "Good morning",
    "today.greet.afternoon": "Good afternoon",
    "today.greet.evening": "Good evening",
    "today.defaultName": "friend",
    "today.agendaEmpty": "Nothing scheduled.",
    "today.habitsEmpty": "Nothing scheduled for today.",
    "today.habitsScheduled": "{count} for today",
    "today.eventsCount": "{count} events",
    "today.recentNotes": "{count} recent",
    "today.quickCapture": "Quick capture",
    "today.summary.events.one": "{count} event",
    "today.summary.events.few": "{count} events",
    "today.summary.events.many": "{count} events",
    "today.summary.events.other": "{count} events",
    "today.summary.habits.one": "{count} habit",
    "today.summary.habits.few": "{count} habits",
    "today.summary.habits.many": "{count} habits",
    "today.summary.habits.other": "{count} habits",

    // ---- Lib (NoteChip etc.) ----
    "lib.noteEmpty": "(empty note)",
    "lib.noteLinkLoading": "[[note:loading…]]",
    "lib.noteTitle": "Note {id}",

    // ---- Tasks ----
    "tasks.empty": "No task lists yet. Create one to get started.",
    "tasks.newList": "New list",
    "tasks.addList": "New list",
    "tasks.doneSection": "done",
    "tasks.addTaskPlaceholder": "Add a task…",
    "tasks.newTaskLabel": "New task",
    "tasks.listNamePlaceholder": "List name…",
    "tasks.newListNameLabel": "New list name",
    "tasks.filterListsPlaceholder": "filter lists…",
    "tasks.rail.pinned": "pinned",
    "tasks.rail.projects": "projects",
    "tasks.rail.unassigned": "unassigned",
    "tasks.pinAria": "Pin list",
    "tasks.unpinAria": "Unpin list",
    "tasks.listNameLabel": "name",
    "tasks.listColorLabel": "color",
    "tasks.listColor.auto": "auto",
    "tasks.listColor.purple": "purple",
    "tasks.listColor.blue": "blue",
    "tasks.listColor.green": "green",
    "tasks.listColor.orange": "orange",
    "tasks.listColor.yellow": "yellow",
    "tasks.listColor.aqua": "aqua",
    "tasks.listColor.red": "red",
    "tasks.listColor.fg": "neutral",
    "tasks.listSectionLabel": "section",
    "tasks.listPinLabel": "pin to top",
    "tasks.listDefaultTagsLabel": "default tags",
    "tasks.listDefaultTagsPlaceholder": "tags auto-applied to new tasks…",
    "tasks.listDescriptionLabel": "description",
    "tasks.listDescriptionPlaceholder": "Notes about this list…",
    "tasks.listDescriptionMarkdownHint": "markdown",
    "tasks.sections.manage": "manage sections…",
    "tasks.settings.title": "list sections",
    "tasks.settings.newSection": "new section name…",
    "tasks.settings.sectionNameLabel": "Section name",
    "tasks.settings.deleteSectionConfirm": "Delete this section? Lists move to 'unassigned'.",
    "tasks.deleteListConfirm": "Delete this list and all its tasks?",
    "tasks.archive": "archive",
    "tasks.restore": "restore",
    "tasks.archive.title": "archived lists",
    "tasks.archive.empty": "No archived lists yet.",
    "tasks.due": "due",
    "tasks.due.today": "due today",
    "tasks.due.tomorrow": "due tomorrow",
    "tasks.due.yesterday": "due yesterday",
    "tasks.due.inDays": "due in {count}d",
    "tasks.due.overdue": "overdue {count}d",
    "tasks.assignedMark": "👤 assigned",
    "tasks.completedMark": "✓ done",
    "tasks.completed.today": "✓ done today",
    "tasks.completed.yesterday": "✓ done yesterday",
    "tasks.completed.daysAgo": "✓ done {count}d ago",
    "tasks.complete": "Complete",
    "tasks.reopen": "Reopen",
    "tasks.completeAria": "Complete {title}",
    "tasks.reopenAria": "Reopen {title}",
    "tasks.deleteAria": "Delete {title}",
    "tasks.titleLabel": "Task title",
    "tasks.descriptionLabel": "Task description",
    "tasks.descriptionPlaceholder": "Notes, links, [[note:id]] chips…",
    "tasks.priority.label": "Priority",
    "tasks.priority.low": "Low",
    "tasks.priority.normal": "Normal",
    "tasks.priority.high": "High",
    "tasks.priority.urgent": "Urgent",
    "tasks.tags.label": "Tags",
    "tasks.tags.placeholder": "comma, separated, tags",
    "tasks.filters.clear": "Clear filters",
    "tasks.filterByTagAria": "Filter by tag {tag}",
    "tasks.openDrawerAria": "Edit {title}",
    "tasks.starts": "starts",
    "tasks.startDateLabel": "Start date",
    "tasks.dueDateLabel": "Due date",
    "tasks.colorLabel": "Color",
    "tasks.deleteConfirm": "Delete this task?",
    "tasks.metaCreated": "created {date}",
    "tasks.metaUpdated": "last edited {date}",
    "tasks.metaCompleted": "completed {date}",

    // ---- Calendar ----
    "calendar.empty": "Nothing scheduled.",
    "calendar.allDay": "all-day",
    "calendar.eventChip": "event",
    "calendar.habitChip": "habit",
    "calendar.dueChip": "due",
    "calendar.taskChip": "task",
    "calendar.newEvent": "New event",
    "calendar.event.titleLabel": "Title",
    "calendar.event.dateLabel": "Date",
    "calendar.event.startLabel": "Start",
    "calendar.event.endLabel": "End",
    "calendar.event.allDay": "All day",
    "calendar.event.descLabel": "Description",
    "calendar.event.descPlaceholder": "Notes, links, [[note:id]]…",
    "calendar.event.endsBeforeStart": "End must be after start.",
    "calendar.event.colorLabel": "color",
    "calendar.event.editLabel": "Edit event",
    "calendar.event.deleteConfirm": "Delete this event?",
    "calendar.event.typeLabel": "type",
    "calendar.event.typeEvent": "event",
    "calendar.event.typeAllDay": "all-day",
    "calendar.event.tagsLabel": "tags",
    "calendar.event.notesLabel": "notes",
    "calendar.event.locationLabel": "location",
    "calendar.event.locationPlaceholder": "place, link, room…",
    "calendar.previousMonth": "previous month",
    "calendar.nextMonth": "next month",
    "calendar.weekday.mon": "Mon",
    "calendar.weekday.tue": "Tue",
    "calendar.weekday.wed": "Wed",
    "calendar.weekday.thu": "Thu",
    "calendar.weekday.fri": "Fri",
    "calendar.weekday.sat": "Sat",
    "calendar.weekday.sun": "Sun",
    "calendar.moreEvents": "+{count} more",
    "calendar.habitOverflow": "+{count} more",
    "calendar.habit.openAria": "Open {name} habit details",
    "calendar.habit.state.scheduled": "Scheduled",
    "calendar.habit.state.partial": "{count}/{target} done",
    "calendar.habit.state.met": "Done",
    "calendar.habit.state.missed": "Missed",
    "calendar.recurring": "Recurring",
    "calendar.repeat.label": "Repeat",
    "calendar.repeat.none": "Does not repeat",
    "calendar.repeat.daily": "Every day",
    "calendar.repeat.weekly": "Every week",
    "calendar.repeat.monthly": "Every month",
    "calendar.repeat.yearly": "Every year",
    "calendar.repeat.custom": "Custom rule: {rule}",
    "calendar.repeat.onDays": "On days",
    "calendar.repeat.monthlyDay": "On day {day}",
    "calendar.repeat.monthlyLast": "On the last day",
    "calendar.repeat.lastDayHint":
      'Months without day {day} would be skipped — "last day" is safer.',
    "calendar.repeat.leapHint": "In non-leap years it falls on 28 February.",
    "calendar.repeat.ends": "Ends",
    "calendar.repeat.endsNever": "Never",
    "calendar.repeat.endsOn": "On date",
    "calendar.repeat.endsAfter": "After N times",
    "calendar.repeat.count": "Times",
    "calendar.repeat.ownerOnly": "Only the owner can change the repeat rule.",
    "calendar.reminder.label": "Reminders",
    "calendar.reminder.add": "Add",
    "calendar.reminder.remove": "Remove reminder {label}",
    "calendar.reminder.atStart": "At start",
    "calendar.reminder.minutes": "{count} min before",
    "calendar.reminder.hours": "{count} h before",
    "calendar.reminder.days": "{count} d before",
    "calendar.reminder.weeks": "{count} wk before",
    "calendar.reminder.dayOf": "On the day at {time}",
    "calendar.reminder.daysBefore": "{count} d before at {time}",
    "calendar.reminder.daysLabel": "Days before",
    "calendar.reminder.timeLabel": "Reminder time",
    "calendar.reminder.hint":
      "Reminders are personal, sent via Telegram, and apply to every occurrence.",
    "calendar.reminder.save": "Save reminders",
    "calendar.invalid_recurrence": "This repeat rule is not supported.",
    "calendar.reminder_limit": "Up to 3 reminders per event.",
    "calendar.invalid_reminder": "This reminder time is not allowed for this event.",
    "calendar.range_too_large": "The date range is too large.",
    "calendar.series_date_change":
      "To move the series to other days, change the weekdays in Repeat.",
    "calendar.invalid_timezone": "Unknown time zone.",
    "tasks.unknown_status": "Unknown list status.",
    "tasks.unknown_priority": "Unknown priority.",
    "shopping.unknown_status": "Unknown list status.",
    "links.unknown_entity_kind": "Unknown item type.",
    "links.invalid_query": "Choose what to show links for.",
    "habits.invalid_history_window": "This date range is not available.",
    "attachments.too_large": "The file is too large.",
    "attachments.invalid_order": "Could not reorder attachments.",
    "auth.unsupported_timezone": "Unknown time zone.",
    "tags.invalid_color": "This tag color is not allowed.",
    "auth.forbidden_for_role": "You do not have access to this section.",
    "preferences.type_mismatch": "This value has the wrong type for this setting.",
    "calendar.not_found": "This event no longer exists.",
    "calendar.invalid_time": "The event must end after it starts.",
    "calendar.series_only_field": "This change applies to the whole series.",
    "calendar.scope.editTitle": "Edit recurring event",
    "calendar.scope.deleteTitle": "Delete recurring event",
    "calendar.scope.this": "Only this event",
    "calendar.scope.following": "This and following events",
    "calendar.scope.all": "All events",
    "calendar.scope.cancelThis": "Cancel this occurrence",
    "calendar.scope.dateHint":
      "To move the whole series to other days, change the days in Repeat.",
    "calendar.scope.mixedChange":
      "Moving this event to another date can't be combined with changing the repeat, all-day setting or tags. Save these changes separately.",
    "calendar.read_only": "You can't change this — ask the owner.",

    // ---- Habits ----
    "habits.newHabit": "New habit",
    "habits.activeCount": "active",
    "habits.empty": "No habits yet.",
    "habits.tab.today": "today",
    "habits.tab.all": "all habits",
    "habits.tod.morning": "morning",
    "habits.tod.afternoon": "afternoon",
    "habits.tod.evening": "evening",
    "habits.tod.allDay": "all day",
    "habits.todayEmpty": "No habits scheduled today.",
    "habits.cadence.mo": "m",
    "habits.cadence.tu": "t",
    "habits.cadence.we": "w",
    "habits.cadence.th": "t",
    "habits.cadence.fr": "f",
    "habits.cadence.sa": "s",
    "habits.cadence.su": "s",
    "habits.heatmapTitle": "last 8 weeks · all habits",
    "habits.namePlaceholder": "Habit name…",
    "habits.nameLabel": "Habit name",
    "habits.descriptionPlaceholder": "Description (optional)",
    "habits.descriptionLabel": "Habit description",
    "habits.lastWeeks": "last 8 weeks",
    "habits.heatmapPlaceholder":
      "Backend history endpoint lands later — no faked data here.",
    "habits.streakLabel": "streak",
    "habits.todayProgress": "{count}/{target} today",
    "habits.markDone": "Mark done",
    "habits.markAria": "Mark {name} as done",
    "habits.deleteConfirm": "Delete this habit and all its check-ins?",
    "habits.form.basics": "Basics",
    "habits.form.color": "Color",
    "habits.form.target": "Target",
    "habits.form.targetPerDay": "per day",
    "habits.schedule.title": "Schedule",
    "habits.schedule.everyday": "Every day",
    "habits.schedule.weekdays": "Specific days",
    "habits.schedule.custom": "Custom",
    "habits.schedule.customLabel": "Custom RRULE",
    "habits.schedule.day.mo": "Mo",
    "habits.schedule.day.tu": "Tu",
    "habits.schedule.day.we": "We",
    "habits.schedule.day.th": "Th",
    "habits.schedule.day.fr": "Fr",
    "habits.schedule.day.sa": "Sa",
    "habits.schedule.day.su": "Su",
    "habits.reminder.title": "Reminder",
    "habits.reminder.enable": "Send a reminder",
    "habits.reminder.time": "Reminder time",
    "habits.reminder.channel.telegram": "via Telegram",
    "habits.reminder.linkTelegramHint":
      "Link Telegram in Settings to receive reminders. Email is coming.",
    "habits.stats.currentStreak": "current streak",
    "habits.stats.longestStreak": "longest",
    "habits.stats.completion30d": "30-day rate",
    "habits.stats.total": "total check-ins",
    "habits.heatmap.notScheduled": "Greyed cells = not scheduled.",

    // ---- Shopping ----
    "shopping.newList": "New list",
    "shopping.addList": "+ list",
    "shopping.emptyLists":
      "No shopping lists yet. Create one to get started.",
    "shopping.emptyList": "List is empty.",
    "shopping.addItemPlaceholder": "Add an item…",
    "shopping.newItemLabel": "New item",
    "shopping.listNamePlaceholder": "List name…",
    "shopping.newListNameLabel": "New list name",
    "shopping.toggleItem": "Toggle {name}",
    "shopping.deleteItem": "Delete {name}",
    "shopping.railSection": "lists",
    "shopping.filterPlaceholder": "filter lists…",
    "shopping.filterLabel": "Filter lists",
    "shopping.progress": "{done} of {total} done",
    "shopping.itemNameLabel": "name",
    "shopping.qtyLabel": "quantity",
    "shopping.unitLabel": "unit",
    "shopping.listNameLabel": "name",
    "shopping.listSectionLabel": "section",
    "shopping.listPinLabel": "pin to top",
    "shopping.pinAria": "Pin list",
    "shopping.unpinAria": "Unpin list",
    "shopping.rail.pinned": "pinned",
    "shopping.rail.unassigned": "unassigned",
    "shopping.sections.manage": "manage sections…",
    "shopping.archive": "archive",
    "shopping.restore": "restore",
    "shopping.archive.title": "archived lists",
    "shopping.archive.empty": "No archived lists yet.",
    "shopping.settings.title": "list sections",
    "shopping.settings.newSection": "new section name…",
    "shopping.settings.deleteSectionConfirm":
      "Delete this section? Lists move to 'unassigned'.",
    "shopping.deleteListConfirm": "Delete this list and all its items?",
    "shopping.deleteItemConfirm": "Delete this item?",
    "shopping.done": "Done",
    "shopping.undo": "Undo",
    "shopping.doneAria": "Mark \"{name}\" as bought",
    "shopping.undoAria": "Unmark \"{name}\"",
    "shopping.editAria": "Edit \"{name}\"",
    "shopping.filterByTagAria": "Filter by tag {tag}",
    "shopping.filters.clear": "Clear filters",
    "shopping.removeTagFilterAria": "Remove tag {tag} from filter",
    "shopping.removeCategoryFilterAria": "Remove category {category} from filter",
    "edit.shopList.title": "edit list",

    // Item edit drawer (Phase 2 expansion)
    "shopping.categoryLabel": "category",
    "shopping.category.none": "— none —",
    "shopping.category.produce": "produce",
    "shopping.category.dairy": "dairy",
    "shopping.category.bakery": "bakery",
    "shopping.category.meat": "meat",
    "shopping.category.frozen": "frozen",
    "shopping.category.pantry": "pantry",
    "shopping.category.drinks": "drinks",
    "shopping.category.household": "household",
    "shopping.category.personal": "personal",
    "shopping.category.other": "other",
    "shopping.priorityLabel": "priority",
    "shopping.priority.low": "low",
    "shopping.priority.normal": "normal",
    "shopping.priority.high": "high",
    "shopping.priority.urgent": "urgent",
    "shopping.brandLabel": "brand",
    "shopping.brandPlaceholder": "preferred brand (optional)",
    "shopping.descriptionLabel": "description",
    "shopping.descriptionPlaceholder": "notes about this item",
    "shopping.priceLabel": "price",
    "shopping.pricePlaceholder": "0.00",
    "shopping.tagsLabel": "tags",
    "shopping.linksLabel": "links",
    "shopping.imageLabel": "image",
    "shopping.colorLabel": "color",
    "shopping.colorAuto": "auto (no color)",
    "shopping.metaCreated": "created · {date}",
    "shopping.metaUpdated": "updated · {date}",

    // List settings + toolbar (Phase 2)
    "shopping.list.storeLabel": "store",
    "shopping.list.storePlaceholder": "Lidl, Costco…",
    "shopping.list.currencyLabel": "currency",
    "shopping.list.currencyPlaceholder": "EUR",
    "shopping.list.budgetCapLabel": "budget",
    "shopping.list.budgetCapPlaceholder": "0.00",
    "shopping.list.totalLabel": "total",
    "shopping.list.budgetBar": "{spent} / {cap} {currency}",
    "shopping.list.budgetOver": "over budget by {amount} {currency}",
    "shopping.groupBy.label": "group",
    "shopping.groupBy.none": "none",
    "shopping.groupBy.category": "category",
    "shopping.groupBy.tag": "tag",
    "shopping.groupBy.aisle": "aisle",
    "shopping.sort.label": "sort",
    "shopping.sort.position": "position",
    "shopping.sort.priority": "priority",
    "shopping.sort.price": "price",
    "shopping.filterChips.label": "filter",
    "shopping.uncategorised": "uncategorised",
    "shopping.untagged": "untagged",

    // Generic upload primitive (used by ImageDropzone — wired into
    // shopping in Phase 4, reusable by future modules).
    "upload.dragDrop": "Drag, paste, or click to upload an image",
    "upload.uploading": "Uploading…",
    "upload.tooLarge": "File is too large.",
    "upload.badType": "Unsupported image type.",
    "upload.remove": "Remove image",
    "upload.failed": "Upload failed. Try again.",

    // Generic attachment gallery (used by `<AttachmentGallery>` —
    // mounts inside the per-entity edit drawers in follow-up PRs).
    "attachments.add": "Add image",
    "attachments.empty": "No images yet.",
    "attachments.uploadFailed": "Upload failed. Try again.",
    "attachments.deleteConfirm": "Delete this image?",
    "attachments.openImage": "Open image",
    "attachments.lightboxClose": "Close",
    "attachments.prevImage": "Previous image",
    "attachments.nextImage": "Next image",

    // ---- Recipes ----
    "recipes.empty": "No recipes yet.",
    "recipes.close": "Close",
    "recipes.timeMeta": "{minutes} min · {servings} serv",
    "recipes.allRecipes": "All recipes",
    "recipes.minutes": "{count} min",
    "recipes.serves": "serves {count}",
    "recipes.ingredients": "Ingredients",
    "recipes.steps": "Steps",
    "recipes.sendToShopping": "Send to shopping",
    "recipes.targetListLabel": "Target shopping list",
    "recipes.pickListOption": "— pick a list —",
    "recipes.pickListFirst": "Pick a list first.",
    "recipes.copy": "Copy",
    "recipes.itemsAdded.one": "Added {count} item.",
    "recipes.itemsAdded.few": "Added {count} items.",
    "recipes.itemsAdded.many": "Added {count} items.",
    "recipes.itemsAdded.other": "Added {count} items.",

    // Recipes Phase 1 (parity)
    "recipes.newRecipe": "New recipe",
    "recipes.titleLabel": "title",
    "recipes.descriptionLabel": "description",
    "recipes.servingsLabel": "servings",
    "recipes.prepLabel": "prep min",
    "recipes.cookLabel": "cook min",
    "recipes.ingredientsLabel": "ingredients",
    "recipes.stepsLabel": "steps",
    "recipes.tagsLabel": "tags",
    "recipes.colorLabel": "color",
    "recipes.colorAuto": "auto (no color)",
    "recipes.linksLabel": "links",
    "recipes.ingredientPlaceholder": "ingredient",
    "recipes.qtyPlaceholder": "qty",
    "recipes.unitPlaceholder": "unit",
    "recipes.stepPlaceholder": "step",
    "recipes.addIngredient": "+ add ingredient",
    "recipes.addStep": "+ add step",
    "recipes.linksAfterSave": "Available after first save.",
    "recipes.deleteConfirm": "Delete this recipe?",
    "recipes.metaCreated": "created · {date}",
    "recipes.metaUpdated": "updated · {date}",
    "recipes.editAria": 'Edit "{title}"',
    "recipes.filterByTagAria": "Filter recipes by tag {tag}",
    "recipes.shopExisting": "Existing list",
    "recipes.shopNew": "New list",
    "recipes.newListNameLabel": "New list name",
    "recipes.createAndCopy": "Create & copy",
    "recipes.noListsHint": "No shopping lists yet — switch to New list.",

    // ---- Settings ----
    "settings.sub": "Profile · Integrations · Appearance",
    "settings.profile": "Profile",
    "settings.profile.section": "Profile + sharing",
    "settings.profile.displayName": "Display name",
    "settings.profile.displayNameHelp": "Visible to anyone you connect with. Empty falls back to your email handle.",
    "settings.profile.avatar": "Avatar",
    "settings.profile.shareCode": "Your share code",
    "settings.profile.shareCodeHelp": "Friends use this to add you. Rotate if it leaks.",
    "settings.profile.copy": "Copy",
    "settings.profile.copied": "Copied",
    "settings.profile.rotate": "Rotate",
    "settings.profile.rotateConfirm": "Generate a new share code? Anyone holding the old one will no longer be able to add you.",
    "settings.profile.save": "Save",
    "settings.profile.saved": "Saved",
    // People (friendships)
    "people.empty": "No friends yet.",
    "people.addFriend": "+ Add friend",
    "people.tabs.all": "All",
    "people.tabs.friends": "Friends",
    "people.tabs.family": "Family",
    "people.tabs.requests": "Requests",
    "people.tabs.blocked": "Blocked",
    "people.tier.friend": "Friend",
    "people.tier.family": "Family",
    "people.promoteToFamily": "Promote to family",
    "people.demoteToFriend": "Demote to friend",
    "people.unfriend": "Unfriend",
    "people.unfriendConfirm": "Remove this connection? Anything they shared with you will disappear.",
    "people.block": "Block",
    "people.unblock": "Unblock",
    "people.blockConfirm": "Block this user? They won't be able to find or add you.",
    "people.since": "since {date}",
    "people.addCode": "Paste their share code",
    "people.codePlaceholder": "XXXX-XXXX",
    "people.send": "Send request",
    "people.errors.codeUnknown": "No user has that code.",
    "people.errors.selfRequest": "That's your own code.",
    "people.errors.requestPending": "A request between you is already pending.",
    "people.errors.alreadyConnected": "You're already connected.",
    "people.requests.incoming": "Incoming",
    "people.requests.outgoing": "Outgoing",
    "people.requests.empty": "No pending requests.",
    "people.accept": "Accept",
    "people.decline": "Decline",
    "people.activity.now": "active now",
    "people.activity.today": "active today",
    "people.activity.thisWeek": "active this week",
    "people.activity.long": "active a while ago",
    "people.activity.action.now": "doing something now",
    "people.activity.action.today": "did something today",
    "people.activity.action.thisWeek": "did something this week",
    "people.activity.action.long": "did something a while ago",
    // Shares (cross-module sharing of resources)
    "shares.section": "Shared with",
    "shares.shareWith": "+ Share with…",
    "shares.access.view": "View",
    "shares.access.edit": "Edit",
    "shares.revoke": "Revoke share",
    "shares.sharedBy": "shared by {name}",
    "shares.friendFallback": "Friend",
    "shares.sharedByBadge.openAria": "Who shared this",
    "shares.sharedByBadge.viewProfile": "View profile",
    "shares.sharedByBadge.access.view": "Read-only access",
    "shares.sharedByBadge.access.edit": "Edit access",
    "shares.readOnly": "Read-only",
    "shares.readOnlyBanner": "Read-only access — you can view but not edit. Changes won't be saved.",
    "shares.scope.mine": "Mine",
    "shares.scope.shared": "Shared",
    "shares.scope.all": "All",
    "shares.alreadyShared": "All your connections already have access.",
    "shares.errors.notConnected": "You're not connected to that person.",
    "shares.errors.notOwner": "Only the owner can share this.",
    "shares.errors.selfShare": "Can't share with yourself.",
    "shares.unsupported_kind": "This item can't be shared.",
    "attachments.unsupported_kind": "Images can't be attached to this item.",
    "settings.preferences": "Preferences",
    "settings.account": "Account",
    "settings.appearance": "Appearance",
    "settings.theme": "Theme",
    "settings.language": "Language",
    "settings.integrations": "Integrations",
    "settings.activity.title": "Activity",
    "settings.activity.toggleLabel":
      "Let friends and family see when I was last active",
    "settings.activity.helpText":
      "Shown as 'active today / this week / a while ago'. Exact times are never shared.",
    "settings.tg.connected_as": "Connected as",
    "settings.tg.connected_meta": "capture & reminders",
    "settings.tg.about":
      "Capture notes, tick habits, get reminders — all from Telegram.",
    "settings.tg.recent": "Recent activity",
    "settings.tg.live": "live",
    "settings.tg.bot": "Telegram bot — what it does",
    "settings.tg.linkCode": "link code",
    "settings.tg.copy": "copy",
    "settings.tg.openTelegram": "Open Telegram",
    "settings.tg.dmInstructions":
      "DM the bot the line above to attach this account.",
    "settings.tg.openInstructions":
      "Tap Open Telegram, then press Start in the chat to attach this account.",
    "settings.tg.itemProposals.title": "Suggest items from notes",
    "settings.tg.itemProposals.description":
      "When on, Vuzlyk analyzes notes you send to the bot and DMs you tasks, shopping lists, and events to confirm with one tap.",
    "settings.tg.itemProposals.error": "Couldn't save. Try again.",
    "settings.gcal": "Google Calendar",
    "settings.gcal.desc": "Two-way sync with your existing calendars.",
    "settings.about": "About",
    "settings.connect": "Connect",
    "settings.disconnect": "Disconnect",
    "settings.botCommands.note.arg": "any text",
    "settings.botCommands.note.help":
      "saves a note, auto-tags by /command",
    "settings.botCommands.habit.arg": "name",
    "settings.botCommands.habit.help": "ticks today's habit",
    "settings.botCommands.today.help":
      "reads back today's agenda + habits",
    "settings.botCommands.at.arg": 'tomorrow 10am "thing"',
    "settings.botCommands.at.help": "drops it on the calendar",

    // ---- Account ----
    "account.timezone.label": "Time zone",
    "account.timezone.help":
      "Used to fire habit reminders at your local time.",

    // ---- Mentions ----
    "mention.placeholder": "Type [[ to mention…",
    "mention.kind.note": "Note",
    "mention.kind.task": "Task",
    "mention.kind.event": "Event",
    "mention.kind.habit": "Habit",
    "mention.kind.shopping_list": "List",
    "mention.kind.shopping_item": "Item",
    "mention.kind.recipe": "Recipe",
    "mention.empty": "No matches.",
    "mention.loading": "Searching…",
    "mention.openAria": "Open {kind} '{title}'",

    // ---- Tags ----
    "tags.placeholder": "add tag…",
    "tags.createNewSuggestion": "+ create '{name}'",
    "tags.colorLabel": "Color",
    "tags.deleteConfirm":
      "Delete tag '{name}'? It will be removed from {count} items.",
    "tags.renameCollision": "A tag named '{name}' already exists.",
    "tags.usage.one": "{count} item",
    "tags.usage.few": "{count} items",
    "tags.usage.many": "{count} items",
    "tags.usage.other": "{count} items",
    "settings.tags": "Tags",
    "settings.tags.empty": "No tags yet.",
    "notes.tags.label": "Tags",
    "habits.tagsLabel": "Tags",

    // ---- Admin operator surface ----
    "auth.accountDeactivated":
      "This account is deactivated. Contact support if you believe this is a mistake.",
    "admin.nav.label": "Admin",
    "admin.users.title": "Admin · Users",
    "admin.users.search.placeholder": "Search email or display name…",
    "admin.users.search.submit": "Search",
    "admin.users.filter.role": "Role",
    "admin.users.filter.status": "Status",
    "admin.users.empty": "No users match these filters.",
    "admin.users.columns.email": "Email",
    "admin.users.columns.display_name": "Display name",
    "admin.users.columns.role": "Role",
    "admin.users.columns.status": "Status",
    "admin.users.columns.signup": "Signed up",
    "admin.users.columns.last_seen": "Last seen",
    "admin.users.status.active": "Active",
    "admin.users.status.deactivated": "Deactivated",
    "admin.users.status.all": "All",
    "admin.role.user": "User",
    "admin.role.admin": "Admin",
    "admin.role.superadmin": "Superadmin",
    "admin.detail.backToList": "← Back to users",
    "admin.detail.section.identity": "Identity",
    "admin.detail.section.stats": "Statistics",
    "admin.detail.section.actions": "Actions",
    "admin.detail.section.audit": "Recent activity",
    "admin.stats.notes": "Notes",
    "admin.stats.tasks": "Tasks (active)",
    "admin.stats.task_lists": "Task lists (active)",
    "admin.stats.calendar_events": "Calendar events",
    "admin.stats.habits": "Habits (active)",
    "admin.stats.recipes": "Recipes",
    "admin.stats.shopping_lists": "Shopping lists (active)",
    "admin.stats.shopping_items": "Shopping items (active)",
    "admin.stats.tags": "Tags",
    "admin.stats.attachments": "Attachments",
    "admin.stats.connections": "Connections",
    "admin.stats.storage_bytes": "Storage used",
    "admin.stats.llm_tokens": "LLM tokens (in / out)",
    "admin.stats.llm_calls": "LLM calls",
    "admin.stats.llm_cost": "Estimated LLM cost",
    "admin.stats.signup": "Signed up",
    "admin.stats.last_seen": "Last seen",
    "admin.stats.last_action": "Last action",
    "admin.actions.deactivate": "Deactivate",
    "admin.actions.reactivate": "Reactivate",
    "admin.actions.change_role": "Change role",
    "admin.confirm.deactivate": "Deactivate account",
    "admin.confirm.reactivate":
      "Reactivate this account? The user will be able to sign in again.",
    "admin.confirm.changeRole": "Apply new role",
    "admin.deactivate.reason_label": "Reason",
    "admin.deactivate.reason_required":
      "Required — at least 3 characters. Goes into the audit log.",
    "admin.audit.title": "Admin · Audit log",
    "admin.audit.empty": "No entries.",
    "admin.audit.filter.action": "Action",
    "admin.audit.columns.when": "When",
    "admin.audit.columns.action": "Action",
    "admin.audit.columns.actor": "Actor",
    "admin.audit.columns.target": "Target",
    "admin.audit.columns.reason": "Reason",
    "admin.audit.actions.user_deactivate": "Deactivated user",
    "admin.audit.actions.user_reactivate": "Reactivated user",
    "admin.audit.actions.user_role_change": "Changed user role",
    "admin.audit.actions.bootstrap_promote": "Bootstrap promotion",
    "admin.pagination.prev": "Prev",
    "admin.pagination.next": "Next",
    "admin.pagination.pageOf": "Page {page} of {total}",
    "admin.errors.lastSuperadmin":
      "Cannot demote the last superadmin. Promote another account first.",
    "admin.errors.cannotSelfDeactivate":
      "You cannot deactivate your own account.",
    "admin.errors.alreadyDeactivated": "This account is already deactivated.",
    "admin.errors.notDeactivated": "This account is not deactivated.",
    "admin.errors.noopRoleChange": "User already has this role.",
    "admin.errors.userNotFound": "User not found.",
} as const;

const UK_STRINGS: Partial<Record<keyof typeof EN_STRINGS, string>> = {
    appTitle: "Vuzlyk",
    appTagline: "день за днем, повʼязані разом",

    today: "Сьогодні",
    tasks: "Задачі",
    notes: "Нотатки",
    cal: "Календар",
    habit: "Звички",
    shop: "Покупки",
    cook: "Рецепти",
    settings: "Налаштування",
    people: "Люди",
    "nav.apps": "Додатки",
    "nav.system": "Система",

    "shell.openMenu": "Відкрити меню навігації", // REVIEW

    signIn: "Увійти",
    signUp: "Реєстрація",
    signOut: "Вийти",
    email: "Email",
    password: "Пароль",
    noAccountYet: "Ще немає акаунту?",
    alreadyHaveAccount: "Вже маєте акаунт?",
    forgotPassword: "Забули пароль?",
    resetPassword: "Скинути пароль",
    resetEmailSent:
      "Якщо така адреса існує — посилання для скидання вже летить до вашої скриньки.", // REVIEW
    newPassword: "Новий пароль",
    confirmPassword: "Підтвердьте пароль",
    passwordsDoNotMatch: "Паролі не співпадають.",
    resetLinkInvalid: "Це посилання вже недійсне або не існує.", // REVIEW
    requestNewResetLink: "Надіслати ще раз",
    passwordResetSuccess:
      "Пароль оновлено. Увійдіть зі своїм новим паролем.", // REVIEW
    backToLogin: "Назад до входу",
    finishingSignIn: "Завершуємо вхід…",
    signInWithGoogle: "Увійти через Google",
    "auth.passwordHint": "Мінімум 12 символів.", // REVIEW
    "auth.signInError": "Помилка входу", // REVIEW

    connectGoogle: "Підʼєднати Google",
    disconnectGoogle: "Відʼєднати",
    googleConnectedAs: "Підʼєднано як",
    googleNotConnected: "Не підʼєднано",
    googleAccount: "Google-акаунт",
    googleErrorCancelled: "Вхід через Google було скасовано.", // REVIEW
    googleErrorAlreadyLinked:
      "Цей Google-акаунт уже підʼєднано до іншого користувача.", // REVIEW
    googleErrorEmailUnverified:
      "Увійдіть із паролем і підʼєднайте Google з налаштувань.", // REVIEW
    googleErrorProvider:
      "Не вдалося звʼязатися з Google. Спробуйте ще раз.", // REVIEW
    googleErrorIdToken:
      "Відповідь Google невалідна. Спробуйте ще раз.", // REVIEW
    googleErrorState: "Спроба входу вже неактуальна. Почніть знову.", // REVIEW
    googleErrorExchange: "Час на завершення входу вийшов. Почніть знову.", // REVIEW
    googleErrorGeneric: "Вхід через Google не вдався.",

    notesEmpty: "Нотаток поки немає — створіть першу нижче.",
    newNotePlaceholder: "Запишіть думку…",
    tagsPlaceholder: "теги,через,кому",
    "notes.allNotes": "Усі нотатки", // REVIEW
    "notes.sendViaTelegram": "Надіслати через Telegram", // REVIEW
    "notes.contentLabel": "Зміст нотатки", // REVIEW
    "notes.tagsLabel": "Теги",
    "notes.searchPlaceholder": "Пошук нотаток…", // REVIEW
    "notes.searchLabel": "Пошук нотаток", // REVIEW
    "notes.newNote": "Нова нотатка", // REVIEW
    "notes.untitledDefault": "Без назви\n\n", // REVIEW
    "notes.emptyTitle": "(порожня)", // REVIEW
    "notes.breadcrumbRoot": "нотатки",
    "notes.titleLabel": "заголовок",
    "notes.titlePlaceholder": "Назва нотатки…",
    "notes.bodyLabel": "текст · markdown",
    "notes.metaEdited": "редаговано {date}",
    "notes.metaCreatedAt": "створено {date}",
    "notes.metaUpdatedAt": "редаговано {date}",
    "notes.metaChars": "{count} симв.",
    "notes.deleteConfirm": "Видалити цю нотатку?",
    "notes.colorLabel": "колір",
    "notes.colorAuto": "авто",
    "links.label": "пов'язані",
    "links.add": "звʼязати",
    "links.remove": "видалити звʼязок",
    "links.searchPlaceholder": "шукати нотатки, задачі, події, звички…",
    "links.noMatches": "нічого не знайдено",
    "links.kind.note": "нотатка",
    "links.kind.task": "задача",
    "links.kind.task_list": "список",
    "links.kind.event": "подія",
    "links.kind.habit": "звичка",
    "links.kind.recipe": "рецепт",
    "links.kind.shopping_list": "покупки",
    "links.kind.shopping_item": "позиція", // REVIEW
    "links.kind.tag": "тег",

    create: "Створити",
    cancel: "Скасувати",
    save: "Зберегти",
    delete: "Видалити",
    edit: "Редагувати",
    open: "відкрити",
    loadMore: "Завантажити ще",
    loading: "Завантаження…",
    somethingWentWrong: "Щось пішло не так",
    comingSoon: "скоро",
    "topbar.searchPlaceholder": "Пошук…",
    "common.soon": "скоро",
    "common.open": "Відкрити", // REVIEW
    "common.close": "Закрити",
    "edit.task.title": "редагувати задачу",
    "edit.event.title": "редагувати подію",
    "edit.habit.title": "редагувати звичку",
    "new.habit.title": "нова звичка",
    "edit.note.title": "редагувати нотатку",
    "edit.shopItem.title": "редагувати позицію",
    "edit.taskList.title": "редагувати список",
    "today.cardOpen": "відкрити",

    "today.greet.morning": "Доброго ранку", // REVIEW
    "today.greet.afternoon": "Доброго дня", // REVIEW
    "today.greet.evening": "Доброго вечора", // REVIEW
    "today.defaultName": "друже", // REVIEW
    "today.agendaEmpty": "Нічого не заплановано.", // REVIEW
    "today.habitsEmpty": "На сьогодні нічого не заплановано.", // REVIEW
    "today.habitsScheduled": "{count} на сьогодні", // REVIEW
    "today.eventsCount": "подій: {count}", // REVIEW
    "today.recentNotes": "{count} нещодавніх", // REVIEW
    "today.quickCapture": "Швидкий запис", // REVIEW
    "today.summary.events.one": "{count} подія", // REVIEW
    "today.summary.events.few": "{count} події", // REVIEW
    "today.summary.events.many": "{count} подій", // REVIEW
    "today.summary.events.other": "{count} події", // REVIEW
    "today.summary.habits.one": "{count} звичка", // REVIEW
    "today.summary.habits.few": "{count} звички", // REVIEW
    "today.summary.habits.many": "{count} звичок", // REVIEW
    "today.summary.habits.other": "{count} звички", // REVIEW

    "lib.noteEmpty": "(порожня нотатка)", // REVIEW
    "lib.noteLinkLoading": "[[нотатка:завантаження…]]", // REVIEW
    "lib.noteTitle": "Нотатка {id}", // REVIEW

    "tasks.empty": "Списків задач ще немає. Створіть перший, щоб почати.", // REVIEW
    "tasks.newList": "Новий список", // REVIEW
    "tasks.addList": "Новий список",
    "tasks.doneSection": "виконано", // REVIEW
    "tasks.addTaskPlaceholder": "Додати задачу…", // REVIEW
    "tasks.newTaskLabel": "Нова задача", // REVIEW
    "tasks.listNamePlaceholder": "Назва списку…", // REVIEW
    "tasks.newListNameLabel": "Назва нового списку", // REVIEW
    "tasks.filterListsPlaceholder": "фільтрувати списки…",
    "tasks.rail.pinned": "закріплені",
    "tasks.rail.projects": "списки",
    "tasks.rail.unassigned": "без розділу",
    "tasks.pinAria": "Закріпити список",
    "tasks.unpinAria": "Відкріпити список",
    "tasks.listNameLabel": "назва",
    "tasks.listColorLabel": "колір",
    "tasks.listColor.auto": "авто",
    "tasks.listColor.purple": "пурпурний",
    "tasks.listColor.blue": "синій",
    "tasks.listColor.green": "зелений",
    "tasks.listColor.orange": "оранжевий",
    "tasks.listColor.yellow": "жовтий",
    "tasks.listColor.aqua": "бірюзовий",
    "tasks.listColor.red": "червоний",
    "tasks.listColor.fg": "нейтральний",
    "tasks.listSectionLabel": "розділ",
    "tasks.listPinLabel": "закріпити вгорі",
    "tasks.listDefaultTagsLabel": "теги за замовчуванням",
    "tasks.listDefaultTagsPlaceholder": "теги, які додаються до нових задач…",
    "tasks.listDescriptionLabel": "опис",
    "tasks.listDescriptionPlaceholder": "Нотатки про цей список…",
    "tasks.listDescriptionMarkdownHint": "markdown",
    "tasks.sections.manage": "керувати розділами…",
    "tasks.settings.title": "розділи списків",
    "tasks.settings.newSection": "назва нового розділу…",
    "tasks.settings.sectionNameLabel": "Назва розділу",
    "tasks.settings.deleteSectionConfirm": "Видалити цей розділ? Списки переміщуються в 'без розділу'.",
    "tasks.deleteListConfirm": "Видалити цей список і всі його задачі?",
    "tasks.archive": "архів",
    "tasks.restore": "відновити",
    "tasks.archive.title": "архівовані списки",
    "tasks.archive.empty": "Архівованих списків поки немає.",
    "tasks.due": "до", // REVIEW
    "tasks.due.today": "до сьогодні", // REVIEW
    "tasks.due.tomorrow": "до завтра", // REVIEW
    "tasks.due.yesterday": "до вчора", // REVIEW
    "tasks.due.inDays": "до за {count}д", // REVIEW
    "tasks.due.overdue": "прострочено на {count}д", // REVIEW
    "tasks.assignedMark": "👤 призначено", // REVIEW
    "tasks.completedMark": "✓ виконано", // REVIEW
    "tasks.completed.today": "✓ виконано сьогодні", // REVIEW
    "tasks.completed.yesterday": "✓ виконано вчора", // REVIEW
    "tasks.completed.daysAgo": "✓ виконано {count}д тому", // REVIEW
    "tasks.complete": "Зарахувати", // REVIEW
    "tasks.reopen": "Відкрити", // REVIEW
    "tasks.completeAria": "Зарахувати «{title}»", // REVIEW
    "tasks.reopenAria": "Відкрити «{title}»", // REVIEW
    "tasks.deleteAria": "Видалити «{title}»", // REVIEW
    "tasks.titleLabel": "Назва задачі", // REVIEW
    "tasks.descriptionLabel": "Опис задачі", // REVIEW
    "tasks.descriptionPlaceholder": "Нотатки, посилання, [[note:id]]…", // REVIEW
    "tasks.priority.label": "Пріоритет", // REVIEW
    "tasks.priority.low": "Низький", // REVIEW
    "tasks.priority.normal": "Звичайний", // REVIEW
    "tasks.priority.high": "Високий", // REVIEW
    "tasks.priority.urgent": "Терміновий", // REVIEW
    "tasks.tags.label": "Теги", // REVIEW
    "tasks.tags.placeholder": "теги, через, кому", // REVIEW
    "tasks.filters.clear": "Скинути фільтри", // REVIEW
    "tasks.filterByTagAria": "Фільтрувати за тегом {tag}", // REVIEW
    "tasks.openDrawerAria": "Редагувати «{title}»", // REVIEW
    "tasks.starts": "початок", // REVIEW
    "tasks.startDateLabel": "Дата початку", // REVIEW
    "tasks.dueDateLabel": "Термін", // REVIEW
    "tasks.colorLabel": "Колір", // REVIEW
    "tasks.deleteConfirm": "Видалити цю задачу?",
    "tasks.metaCreated": "створено {date}",
    "tasks.metaUpdated": "редаговано {date}",
    "tasks.metaCompleted": "завершено {date}",

    "calendar.empty": "Нічого не заплановано.", // REVIEW
    "calendar.allDay": "увесь день", // REVIEW
    "calendar.eventChip": "подія",
    "calendar.habitChip": "звичка", // REVIEW
    "calendar.dueChip": "до", // REVIEW
    "calendar.taskChip": "задача", // REVIEW
    "calendar.newEvent": "Нова подія", // REVIEW
    "calendar.event.titleLabel": "Заголовок", // REVIEW
    "calendar.event.dateLabel": "Дата", // REVIEW
    "calendar.event.startLabel": "Початок", // REVIEW
    "calendar.event.endLabel": "Кінець", // REVIEW
    "calendar.event.allDay": "Увесь день", // REVIEW
    "calendar.event.descLabel": "Опис", // REVIEW
    "calendar.event.descPlaceholder": "Нотатки, посилання, [[note:id]]…", // REVIEW
    "calendar.event.endsBeforeStart": "Кінець має бути пізніше за початок.", // REVIEW
    "calendar.event.colorLabel": "колір",
    "calendar.event.editLabel": "Редагувати подію", // REVIEW
    "calendar.event.deleteConfirm": "Видалити цю подію?", // REVIEW
    "calendar.event.typeLabel": "тип",
    "calendar.event.typeEvent": "подія",
    "calendar.event.typeAllDay": "увесь день",
    "calendar.event.tagsLabel": "теги",
    "calendar.event.notesLabel": "нотатки",
    "calendar.event.locationLabel": "місце",
    "calendar.event.locationPlaceholder": "адреса, посилання, кімната…",
    "calendar.previousMonth": "попередній місяць", // REVIEW
    "calendar.nextMonth": "наступний місяць", // REVIEW
    "calendar.weekday.mon": "Пн",
    "calendar.weekday.tue": "Вт",
    "calendar.weekday.wed": "Ср",
    "calendar.weekday.thu": "Чт",
    "calendar.weekday.fri": "Пт",
    "calendar.weekday.sat": "Сб",
    "calendar.weekday.sun": "Нд",
    "calendar.moreEvents": "+ще {count}", // REVIEW
    "calendar.habitOverflow": "+ще {count}", // REVIEW
    "calendar.habit.openAria": "Відкрити деталі звички «{name}»", // REVIEW
    "calendar.habit.state.scheduled": "Заплановано", // REVIEW
    "calendar.habit.state.partial": "{count}/{target} виконано", // REVIEW
    "calendar.habit.state.met": "Виконано", // REVIEW
    "calendar.habit.state.missed": "Пропущено", // REVIEW
    "calendar.recurring": "Повторюється", // REVIEW
    "calendar.repeat.label": "Повтор", // REVIEW
    "calendar.repeat.none": "Не повторюється", // REVIEW
    "calendar.repeat.daily": "Щодня", // REVIEW
    "calendar.repeat.weekly": "Щотижня", // REVIEW
    "calendar.repeat.monthly": "Щомісяця", // REVIEW
    "calendar.repeat.yearly": "Щороку", // REVIEW
    "calendar.repeat.custom": "Власне правило: {rule}", // REVIEW
    "calendar.repeat.onDays": "У дні", // REVIEW
    "calendar.repeat.monthlyDay": "{day}-го числа", // REVIEW
    "calendar.repeat.monthlyLast": "Останнього дня місяця", // REVIEW
    "calendar.repeat.lastDayHint":
      "У місяцях без {day}-го числа повтор пропуститься — надійніше «останнього дня».", // REVIEW
    "calendar.repeat.leapHint": "У невисокосні роки — 28 лютого.", // REVIEW
    "calendar.repeat.ends": "Закінчується", // REVIEW
    "calendar.repeat.endsNever": "Ніколи", // REVIEW
    "calendar.repeat.endsOn": "У дату", // REVIEW
    "calendar.repeat.endsAfter": "Після N разів", // REVIEW
    "calendar.repeat.count": "Разів", // REVIEW
    "calendar.repeat.ownerOnly": "Лише власник може змінити повтор.", // REVIEW
    "calendar.reminder.label": "Нагадування", // REVIEW
    "calendar.reminder.add": "Додати", // REVIEW
    "calendar.reminder.remove": "Прибрати нагадування {label}", // REVIEW
    "calendar.reminder.atStart": "На початку", // REVIEW
    "calendar.reminder.minutes": "За {count} хв", // REVIEW
    "calendar.reminder.hours": "За {count} год", // REVIEW
    "calendar.reminder.days": "За {count} дн.", // REVIEW
    "calendar.reminder.weeks": "За {count} тижд.", // REVIEW
    "calendar.reminder.dayOf": "У день події о {time}", // REVIEW
    "calendar.reminder.daysBefore": "За {count} дн. о {time}", // REVIEW
    "calendar.reminder.daysLabel": "За скільки днів", // REVIEW
    "calendar.reminder.timeLabel": "Час нагадування", // REVIEW
    "calendar.reminder.hint":
      "Нагадування особисті, приходять у Telegram і діють для всіх повторень.", // REVIEW
    "calendar.reminder.save": "Зберегти нагадування", // REVIEW
    "calendar.invalid_recurrence": "Таке правило повтору не підтримується.", // REVIEW
    "calendar.reminder_limit": "Не більше 3 нагадувань на подію.", // REVIEW
    "calendar.invalid_reminder": "Такий час нагадування недоступний для цієї події.", // REVIEW
    "calendar.range_too_large": "Завеликий діапазон дат.", // REVIEW
    "calendar.series_date_change":
      "Щоб перенести серію на інші дні, змініть дні в «Повтор».", // REVIEW
    "calendar.invalid_timezone": "Невідомий часовий пояс.", // REVIEW
    "tasks.unknown_status": "Невідомий статус списку.", // REVIEW
    "tasks.unknown_priority": "Невідомий пріоритет.", // REVIEW
    "shopping.unknown_status": "Невідомий статус списку.", // REVIEW
    "links.unknown_entity_kind": "Невідомий тип елемента.", // REVIEW
    "links.invalid_query": "Оберіть, для чого показати зв'язки.", // REVIEW
    "habits.invalid_history_window": "Цей діапазон дат недоступний.", // REVIEW
    "attachments.too_large": "Файл завеликий.", // REVIEW
    "attachments.invalid_order": "Не вдалося змінити порядок вкладень.", // REVIEW
    "auth.unsupported_timezone": "Невідомий часовий пояс.", // REVIEW
    "tags.invalid_color": "Цей колір тега недоступний.", // REVIEW
    "auth.forbidden_for_role": "У вас немає доступу до цього розділу.", // REVIEW
    "preferences.type_mismatch": "Значення має неправильний тип для цього налаштування.", // REVIEW
    "calendar.not_found": "Цієї події більше не існує.", // REVIEW
    "calendar.invalid_time": "Подія має закінчуватися після початку.", // REVIEW
    "calendar.series_only_field": "Ця зміна стосується всієї серії.", // REVIEW
    "calendar.scope.editTitle": "Змінити повторювану подію", // REVIEW
    "calendar.scope.deleteTitle": "Видалити повторювану подію", // REVIEW
    "calendar.scope.this": "Лише цю подію", // REVIEW
    "calendar.scope.following": "Цю й наступні події", // REVIEW
    "calendar.scope.all": "Усі події", // REVIEW
    "calendar.scope.cancelThis": "Скасувати це повторення", // REVIEW
    "calendar.scope.dateHint":
      "Щоб перенести всю серію на інші дні, змініть дні в «Повтор».", // REVIEW
    "calendar.scope.mixedChange":
      "Перенесення цієї події на іншу дату не можна поєднати зі зміною повтору, режиму «весь день» чи тегів. Збережіть ці зміни окремо.", // REVIEW
    "calendar.read_only": "Ви не можете це змінити — зверніться до власника.", // REVIEW

    "habits.newHabit": "Нова звичка", // REVIEW
    "habits.activeCount": "активних", // REVIEW
    "habits.empty": "Звичок ще немає.", // REVIEW
    "habits.tab.today": "сьогодні",
    "habits.tab.all": "усі звички",
    "habits.tod.morning": "ранок",
    "habits.tod.afternoon": "день",
    "habits.tod.evening": "вечір",
    "habits.tod.allDay": "цілий день",
    "habits.todayEmpty": "На сьогодні немає звичок.",
    "habits.cadence.mo": "пн",
    "habits.cadence.tu": "вт",
    "habits.cadence.we": "ср",
    "habits.cadence.th": "чт",
    "habits.cadence.fr": "пт",
    "habits.cadence.sa": "сб",
    "habits.cadence.su": "нд",
    "habits.heatmapTitle": "останні 8 тижнів · усі звички", // REVIEW
    "habits.namePlaceholder": "Назва звички…", // REVIEW
    "habits.nameLabel": "Назва звички", // REVIEW
    "habits.descriptionPlaceholder": "Опис (необовʼязково)", // REVIEW
    "habits.descriptionLabel": "Опис звички", // REVIEW
    "habits.lastWeeks": "останні 8 тижнів", // REVIEW
    "habits.heatmapPlaceholder":
      "Графік історії зʼявиться пізніше — підроблених даних тут немає.", // REVIEW
    "habits.streakLabel": "серія", // REVIEW
    "habits.todayProgress": "{count}/{target} сьогодні", // REVIEW
    "habits.markDone": "Зарахувати",
    "habits.markAria": "Зарахувати «{name}»",
    "habits.deleteConfirm": "Видалити цю звичку та всі її відмітки?", // REVIEW
    "habits.form.basics": "Основне", // REVIEW
    "habits.form.color": "Колір", // REVIEW
    "habits.form.target": "Ціль", // REVIEW
    "habits.form.targetPerDay": "на день", // REVIEW
    "habits.schedule.title": "Розклад", // REVIEW
    "habits.schedule.everyday": "Щодня", // REVIEW
    "habits.schedule.weekdays": "Окремі дні", // REVIEW
    "habits.schedule.custom": "Власний", // REVIEW
    "habits.schedule.customLabel": "Власний RRULE", // REVIEW
    "habits.schedule.day.mo": "Пн",
    "habits.schedule.day.tu": "Вт",
    "habits.schedule.day.we": "Ср",
    "habits.schedule.day.th": "Чт",
    "habits.schedule.day.fr": "Пт",
    "habits.schedule.day.sa": "Сб",
    "habits.schedule.day.su": "Нд",
    "habits.reminder.title": "Нагадування", // REVIEW
    "habits.reminder.enable": "Надсилати нагадування", // REVIEW
    "habits.reminder.time": "Час нагадування", // REVIEW
    "habits.reminder.channel.telegram": "через Telegram", // REVIEW
    "habits.reminder.linkTelegramHint":
      "Привʼяжіть Telegram у Налаштуваннях, щоб отримувати нагадування. Email — згодом.", // REVIEW
    "habits.stats.currentStreak": "поточна серія", // REVIEW
    "habits.stats.longestStreak": "найдовша", // REVIEW
    "habits.stats.completion30d": "30-денна частка", // REVIEW
    "habits.stats.total": "усього відміток", // REVIEW
    "habits.heatmap.notScheduled": "Сірі клітинки — не за розкладом.", // REVIEW

    "shopping.newList": "Новий список", // REVIEW
    "shopping.addList": "+ список", // REVIEW
    "shopping.emptyLists":
      "Списків покупок ще немає. Створіть перший, щоб почати.", // REVIEW
    "shopping.emptyList": "Список порожній.", // REVIEW
    "shopping.addItemPlaceholder": "Додати позицію…", // REVIEW
    "shopping.newItemLabel": "Нова позиція", // REVIEW
    "shopping.listNamePlaceholder": "Назва списку…", // REVIEW
    "shopping.newListNameLabel": "Назва нового списку", // REVIEW
    "shopping.toggleItem": "Перемкнути «{name}»", // REVIEW
    "shopping.deleteItem": "Видалити «{name}»", // REVIEW
    "shopping.railSection": "списки",
    "shopping.filterPlaceholder": "фільтр списків…",
    "shopping.filterLabel": "Фільтр списків",
    "shopping.progress": "{done} з {total} виконано",
    "shopping.itemNameLabel": "назва",
    "shopping.qtyLabel": "кількість",
    "shopping.unitLabel": "одиниця",
    "shopping.listNameLabel": "назва",
    "shopping.listSectionLabel": "розділ",
    "shopping.listPinLabel": "закріпити вгорі",
    "shopping.pinAria": "Закріпити список",
    "shopping.unpinAria": "Відкріпити список",
    "shopping.rail.pinned": "закріплені",
    "shopping.rail.unassigned": "без розділу",
    "shopping.sections.manage": "керувати розділами…",
    "shopping.archive": "архів",
    "shopping.restore": "відновити",
    "shopping.archive.title": "архівовані списки",
    "shopping.archive.empty": "Архівованих списків поки немає.",
    "shopping.settings.title": "розділи списків",
    "shopping.settings.newSection": "назва нового розділу…",
    "shopping.settings.deleteSectionConfirm":
      "Видалити цей розділ? Списки переміщуються в 'без розділу'.",
    "shopping.deleteListConfirm": "Видалити цей список і всі його елементи?",
    "shopping.deleteItemConfirm": "Видалити цю позицію?", // REVIEW
    "shopping.done": "Готово", // REVIEW
    "shopping.undo": "Скасувати", // REVIEW
    "shopping.doneAria": "Позначити «{name}» як куплено", // REVIEW
    "shopping.undoAria": "Зняти позначку з «{name}»", // REVIEW
    "shopping.editAria": "Редагувати «{name}»", // REVIEW
    "shopping.filterByTagAria": "Фільтрувати за тегом {tag}", // REVIEW
    "shopping.filters.clear": "Очистити фільтри", // REVIEW
    "shopping.removeTagFilterAria": "Прибрати тег {tag} з фільтра", // REVIEW
    "shopping.removeCategoryFilterAria": "Прибрати категорію {category} з фільтра", // REVIEW
    "edit.shopList.title": "редагувати список",

    // Item edit drawer (Phase 2 expansion)
    "shopping.categoryLabel": "категорія", // REVIEW
    "shopping.category.none": "— немає —", // REVIEW
    "shopping.category.produce": "овочі та фрукти", // REVIEW
    "shopping.category.dairy": "молочні", // REVIEW
    "shopping.category.bakery": "випічка", // REVIEW
    "shopping.category.meat": "мʼясо", // REVIEW
    "shopping.category.frozen": "заморозка", // REVIEW
    "shopping.category.pantry": "бакалія", // REVIEW
    "shopping.category.drinks": "напої", // REVIEW
    "shopping.category.household": "побутове", // REVIEW
    "shopping.category.personal": "особисте", // REVIEW
    "shopping.category.other": "інше", // REVIEW
    "shopping.priorityLabel": "пріоритет", // REVIEW
    "shopping.priority.low": "низький", // REVIEW
    "shopping.priority.normal": "звичайний", // REVIEW
    "shopping.priority.high": "високий", // REVIEW
    "shopping.priority.urgent": "терміновий", // REVIEW
    "shopping.brandLabel": "бренд", // REVIEW
    "shopping.brandPlaceholder": "улюблений бренд (необовʼязково)", // REVIEW
    "shopping.descriptionLabel": "опис", // REVIEW
    "shopping.descriptionPlaceholder": "нотатки про позицію", // REVIEW
    "shopping.priceLabel": "ціна", // REVIEW
    "shopping.pricePlaceholder": "0,00", // REVIEW
    "shopping.tagsLabel": "теги", // REVIEW
    "shopping.linksLabel": "звʼязки", // REVIEW
    "shopping.imageLabel": "зображення", // REVIEW
    "shopping.colorLabel": "колір", // REVIEW
    "shopping.colorAuto": "авто (без кольору)", // REVIEW
    "shopping.metaCreated": "створено · {date}", // REVIEW
    "shopping.metaUpdated": "оновлено · {date}", // REVIEW

    // List settings + toolbar (Phase 2)
    "shopping.list.storeLabel": "магазин", // REVIEW
    "shopping.list.storePlaceholder": "Сільпо, Ашан…", // REVIEW
    "shopping.list.currencyLabel": "валюта", // REVIEW
    "shopping.list.currencyPlaceholder": "UAH", // REVIEW
    "shopping.list.budgetCapLabel": "бюджет", // REVIEW
    "shopping.list.budgetCapPlaceholder": "0,00", // REVIEW
    "shopping.list.totalLabel": "разом", // REVIEW
    "shopping.list.budgetBar": "{spent} / {cap} {currency}", // REVIEW
    "shopping.list.budgetOver": "перевищено бюджет на {amount} {currency}", // REVIEW
    "shopping.groupBy.label": "групувати", // REVIEW
    "shopping.groupBy.none": "без груп", // REVIEW
    "shopping.groupBy.category": "за категорією", // REVIEW
    "shopping.groupBy.tag": "за тегом", // REVIEW
    "shopping.groupBy.aisle": "за рядом полиць", // REVIEW
    "shopping.sort.label": "сортування", // REVIEW
    "shopping.sort.position": "за порядком", // REVIEW
    "shopping.sort.priority": "за пріоритетом", // REVIEW
    "shopping.sort.price": "за ціною", // REVIEW
    "shopping.filterChips.label": "фільтр", // REVIEW
    "shopping.uncategorised": "без категорії", // REVIEW
    "shopping.untagged": "без тегів", // REVIEW

    // Generic upload primitive
    "upload.dragDrop": "Перетягніть, вставте або клікніть, щоб завантажити зображення", // REVIEW
    "upload.uploading": "Завантаження…", // REVIEW
    "upload.tooLarge": "Файл завеликий.", // REVIEW
    "upload.badType": "Непідтримуваний тип зображення.", // REVIEW
    "upload.remove": "Видалити зображення", // REVIEW
    "upload.failed": "Не вдалося завантажити. Спробуйте ще раз.", // REVIEW

    // Generic attachment gallery
    "attachments.add": "Додати фото",
    "attachments.empty": "Поки що жодних зображень.",
    "attachments.uploadFailed": "Не вдалося завантажити. Спробуйте ще раз.",
    "attachments.deleteConfirm": "Видалити це зображення?",
    "attachments.openImage": "Відкрити зображення",
    "attachments.lightboxClose": "Закрити",
    "attachments.prevImage": "Попереднє зображення",
    "attachments.nextImage": "Наступне зображення",

    "recipes.empty": "Рецептів ще немає.", // REVIEW
    "recipes.close": "Закрити",
    "recipes.timeMeta": "{minutes} хв · {servings} порц",
    "recipes.allRecipes": "Усі рецепти", // REVIEW
    "recipes.minutes": "{count} хв", // REVIEW
    "recipes.serves": "порц.: {count}", // REVIEW
    "recipes.ingredients": "Інгредієнти", // REVIEW
    "recipes.steps": "Кроки", // REVIEW
    "recipes.sendToShopping": "Додати до покупок", // REVIEW
    "recipes.targetListLabel": "Цільовий список покупок", // REVIEW
    "recipes.pickListOption": "— оберіть список —", // REVIEW
    "recipes.pickListFirst": "Спочатку оберіть список.", // REVIEW
    "recipes.copy": "Копіювати", // REVIEW
    "recipes.itemsAdded.one": "Додано {count} позицію.", // REVIEW
    "recipes.itemsAdded.few": "Додано {count} позиції.", // REVIEW
    "recipes.itemsAdded.many": "Додано {count} позицій.", // REVIEW
    "recipes.itemsAdded.other": "Додано {count} позиції.", // REVIEW

    "recipes.newRecipe": "Новий рецепт", // REVIEW
    "recipes.titleLabel": "назва", // REVIEW
    "recipes.descriptionLabel": "опис", // REVIEW
    "recipes.servingsLabel": "порції", // REVIEW
    "recipes.prepLabel": "підготовка (хв)", // REVIEW
    "recipes.cookLabel": "готування (хв)", // REVIEW
    "recipes.ingredientsLabel": "інгредієнти", // REVIEW
    "recipes.stepsLabel": "кроки", // REVIEW
    "recipes.tagsLabel": "теги", // REVIEW
    "recipes.colorLabel": "колір", // REVIEW
    "recipes.colorAuto": "авто (без кольору)", // REVIEW
    "recipes.linksLabel": "звʼязки", // REVIEW
    "recipes.ingredientPlaceholder": "інгредієнт", // REVIEW
    "recipes.qtyPlaceholder": "к-сть", // REVIEW
    "recipes.unitPlaceholder": "од.", // REVIEW
    "recipes.stepPlaceholder": "крок", // REVIEW
    "recipes.addIngredient": "+ додати інгредієнт", // REVIEW
    "recipes.addStep": "+ додати крок", // REVIEW
    "recipes.linksAfterSave": "Доступно після першого збереження.", // REVIEW
    "recipes.deleteConfirm": "Видалити цей рецепт?", // REVIEW
    "recipes.metaCreated": "створено · {date}", // REVIEW
    "recipes.metaUpdated": "оновлено · {date}", // REVIEW
    "recipes.editAria": "Редагувати «{title}»", // REVIEW
    "recipes.filterByTagAria": "Фільтрувати рецепти за тегом {tag}", // REVIEW
    "recipes.shopExisting": "Існуючий список", // REVIEW
    "recipes.shopNew": "Новий список", // REVIEW
    "recipes.newListNameLabel": "Назва нового списку", // REVIEW
    "recipes.createAndCopy": "Створити й копіювати", // REVIEW
    "recipes.noListsHint": "Списків покупок ще немає — оберіть «Новий список».", // REVIEW

    "settings.sub": "Профіль · Інтеграції · Вигляд",
    "settings.profile": "Профіль",
    "settings.profile.section": "Профіль + спільне", // REVIEW
    "settings.profile.displayName": "Імʼя", // REVIEW
    "settings.profile.displayNameHelp": "Видно всім вашим контактам. Порожнє — підставляється локальна частина email.", // REVIEW
    "settings.profile.avatar": "Аватар", // REVIEW
    "settings.profile.shareCode": "Ваш код", // REVIEW
    "settings.profile.shareCodeHelp": "Друзі додають вас за цим кодом. Оновіть, якщо потрапив у чужі руки.", // REVIEW
    "settings.profile.copy": "Копіювати", // REVIEW
    "settings.profile.copied": "Скопійовано", // REVIEW
    "settings.profile.rotate": "Оновити", // REVIEW
    "settings.profile.rotateConfirm": "Згенерувати новий код? Старий перестане працювати для всіх, хто його тримає.", // REVIEW
    "settings.profile.save": "Зберегти", // REVIEW
    "settings.profile.saved": "Збережено", // REVIEW
    "people.empty": "Друзів ще немає.", // REVIEW
    "people.addFriend": "+ Додати друга", // REVIEW
    "people.tabs.all": "Усі", // REVIEW
    "people.tabs.friends": "Друзі", // REVIEW
    "people.tabs.family": "Сімʼя", // REVIEW
    "people.tabs.requests": "Запити", // REVIEW
    "people.tabs.blocked": "Заблоковані", // REVIEW
    "people.tier.friend": "Друг", // REVIEW
    "people.tier.family": "Сімʼя", // REVIEW
    "people.promoteToFamily": "Перевести в сімʼю", // REVIEW
    "people.demoteToFriend": "Зробити другом", // REVIEW
    "people.unfriend": "Видалити", // REVIEW
    "people.unfriendConfirm": "Видалити цей звʼязок? Усе, чим людина поділилась, зникне.", // REVIEW
    "people.block": "Заблокувати", // REVIEW
    "people.unblock": "Розблокувати", // REVIEW
    "people.blockConfirm": "Заблокувати? Користувач не зможе вас знайти чи додати.", // REVIEW
    "people.since": "з {date}", // REVIEW
    "people.addCode": "Введіть код друга", // REVIEW
    "people.codePlaceholder": "XXXX-XXXX",
    "people.send": "Надіслати запит", // REVIEW
    "people.errors.codeUnknown": "Немає користувача з таким кодом.", // REVIEW
    "people.errors.selfRequest": "Це ваш власний код.", // REVIEW
    "people.errors.requestPending": "Запит між вами вже існує.", // REVIEW
    "people.errors.alreadyConnected": "Ви вже друзі.", // REVIEW
    "people.requests.incoming": "Вхідні", // REVIEW
    "people.requests.outgoing": "Вихідні", // REVIEW
    "people.requests.empty": "Запитів немає.", // REVIEW
    "people.accept": "Прийняти", // REVIEW
    "people.decline": "Відхилити", // REVIEW
    "people.activity.now": "у мережі", // REVIEW
    "people.activity.today": "був(ла) сьогодні", // REVIEW
    "people.activity.thisWeek": "був(ла) цього тижня", // REVIEW
    "people.activity.long": "був(ла) давно", // REVIEW
    "people.activity.action.now": "щось робить зараз", // REVIEW
    "people.activity.action.today": "щось робив(ла) сьогодні", // REVIEW
    "people.activity.action.thisWeek": "щось робив(ла) цього тижня", // REVIEW
    "people.activity.action.long": "щось робив(ла) давно", // REVIEW
    // Shares
    "shares.section": "Спільний доступ", // REVIEW
    "shares.shareWith": "+ Поділитися з…", // REVIEW
    "shares.access.view": "Перегляд", // REVIEW
    "shares.access.edit": "Редагування", // REVIEW
    "shares.revoke": "Скасувати доступ", // REVIEW
    "shares.sharedBy": "поділив(ла)ся {name}", // REVIEW
    "shares.friendFallback": "Друг(иня)", // REVIEW
    "shares.sharedByBadge.openAria": "Хто поділився", // REVIEW
    "shares.sharedByBadge.viewProfile": "Переглянути профіль", // REVIEW
    "shares.sharedByBadge.access.view": "Лише читання", // REVIEW
    "shares.sharedByBadge.access.edit": "Редагування", // REVIEW
    "shares.readOnly": "Лише читання", // REVIEW
    "shares.readOnlyBanner": "Доступ лише на перегляд — редагувати не можна. Зміни не збережуться.", // REVIEW
    "shares.scope.mine": "Мої", // REVIEW
    "shares.scope.shared": "Спільні", // REVIEW
    "shares.scope.all": "Усі", // REVIEW
    "shares.alreadyShared": "Усі ваші контакти вже мають доступ.", // REVIEW
    "shares.errors.notConnected": "Ви не є друзями з цією людиною.", // REVIEW
    "shares.errors.notOwner": "Поділитися може лише власник.", // REVIEW
    "shares.errors.selfShare": "Не можна поділитися із собою.", // REVIEW
    "shares.unsupported_kind": "Цей елемент не можна поширити.", // REVIEW
    "attachments.unsupported_kind": "До цього елемента не можна додати зображення.", // REVIEW
    "settings.preferences": "Налаштування",
    "settings.account": "Акаунт",
    "settings.appearance": "Вигляд",
    "settings.theme": "Тема",
    "settings.language": "Мова",
    "settings.integrations": "Інтеграції",
    "settings.activity.title": "Активність", // REVIEW
    "settings.activity.toggleLabel":
      "Показувати друзям і родині, коли я був(ла) в мережі", // REVIEW
    "settings.activity.helpText":
      "Відображається як «сьогодні / цього тижня / давно». Точний час ніколи не передається.", // REVIEW
    "settings.tg.connected_as": "Підʼєднано як",
    "settings.tg.connected_meta": "захоплення й нагадування",
    "settings.tg.about":
      "Нотатки, звички, нагадування — усе з Telegram.",
    "settings.tg.recent": "Остання активність",
    "settings.tg.live": "наживо",
    "settings.tg.bot": "Що вміє Telegram-бот",
    "settings.tg.linkCode": "код привʼязки", // REVIEW
    "settings.tg.copy": "копіювати", // REVIEW
    "settings.tg.openTelegram": "Відкрити Telegram", // REVIEW
    "settings.tg.dmInstructions":
      "Надішліть боту рядок вище, щоб привʼязати цей акаунт.", // REVIEW
    "settings.tg.openInstructions":
      "Натисніть «Відкрити Telegram», а потім «Старт» у чаті, щоб привʼязати акаунт.", // REVIEW
    "settings.tg.itemProposals.title": "Підказувати дії з нотаток",
    "settings.tg.itemProposals.description":
      "Коли увімкнено, Vuzlyk аналізує нотатки, які ви надсилаєте боту, і пропонує в Telegram задачі, списки покупок та події — підтвердьте одним дотиком.",
    "settings.tg.itemProposals.error": "Не вдалося зберегти. Спробуйте ще раз.",
    "settings.gcal": "Google Календар",
    "settings.gcal.desc": "Двосторонній синк з вашими календарями.",
    "settings.about": "Про додаток",
    "settings.connect": "Підʼєднати",
    "settings.disconnect": "Відʼєднати",
    "settings.botCommands.note.arg": "будь-який текст", // REVIEW
    "settings.botCommands.note.help":
      "зберігає нотатку, авто-теги через /команду", // REVIEW
    "settings.botCommands.habit.arg": "назва", // REVIEW
    "settings.botCommands.habit.help": "відмічає сьогоднішню звичку", // REVIEW
    "settings.botCommands.today.help":
      "читає сьогоднішній план і звички", // REVIEW
    "settings.botCommands.at.arg": 'завтра 10:00 "подія"', // REVIEW
    "settings.botCommands.at.help": "додає в календар", // REVIEW

    "account.timezone.label": "Часовий пояс", // REVIEW
    "account.timezone.help":
      "Використовується для нагадувань про звички у вашому локальному часі.", // REVIEW

    // ---- Mentions ----
    "mention.placeholder": "Введіть [[ щоб згадати…", // REVIEW
    "mention.kind.note": "Нотатка", // REVIEW
    "mention.kind.task": "Задача", // REVIEW
    "mention.kind.event": "Подія", // REVIEW
    "mention.kind.habit": "Звичка", // REVIEW
    "mention.kind.shopping_list": "Список", // REVIEW
    "mention.kind.shopping_item": "Позиція", // REVIEW
    "mention.kind.recipe": "Рецепт", // REVIEW
    "mention.empty": "Нічого не знайдено.", // REVIEW
    "mention.loading": "Пошук…", // REVIEW
    "mention.openAria": "Відкрити {kind} «{title}»", // REVIEW

    // ---- Tags ----
    "tags.placeholder": "додати тег…", // REVIEW
    "tags.createNewSuggestion": "+ створити «{name}»", // REVIEW
    "tags.colorLabel": "Колір", // REVIEW
    "tags.deleteConfirm":
      "Видалити тег «{name}»? Він зникне з {count} елементів.", // REVIEW
    "tags.renameCollision": "Тег «{name}» уже існує.", // REVIEW
    "tags.usage.one": "{count} елемент", // REVIEW
    "tags.usage.few": "{count} елементи", // REVIEW
    "tags.usage.many": "{count} елементів", // REVIEW
    "tags.usage.other": "{count} елементи", // REVIEW
    "settings.tags": "Теги", // REVIEW
    "settings.tags.empty": "Тегів ще немає.", // REVIEW
    "notes.tags.label": "Теги",
    "habits.tagsLabel": "Теги",

    // ---- Admin operator surface ----
    "auth.accountDeactivated":
      "Цей обліковий запис деактивовано. Звʼяжіться зі службою підтримки, якщо це помилка.",
    "admin.nav.label": "Адмін",
    "admin.users.title": "Адмін · Користувачі",
    "admin.users.search.placeholder": "Пошук за email або іменем…",
    "admin.users.search.submit": "Шукати",
    "admin.users.filter.role": "Роль",
    "admin.users.filter.status": "Стан",
    "admin.users.empty": "Користувачів за фільтром не знайдено.",
    "admin.users.columns.email": "Email",
    "admin.users.columns.display_name": "Імʼя",
    "admin.users.columns.role": "Роль",
    "admin.users.columns.status": "Стан",
    "admin.users.columns.signup": "Зареєстровано",
    "admin.users.columns.last_seen": "Остання активність",
    "admin.users.status.active": "Активний",
    "admin.users.status.deactivated": "Деактивовано",
    "admin.users.status.all": "Усі",
    "admin.role.user": "Користувач",
    "admin.role.admin": "Адмін",
    "admin.role.superadmin": "Суперадмін",
    "admin.detail.backToList": "← До списку користувачів",
    "admin.detail.section.identity": "Профіль",
    "admin.detail.section.stats": "Статистика",
    "admin.detail.section.actions": "Дії",
    "admin.detail.section.audit": "Остання активність",
    "admin.stats.notes": "Нотатки",
    "admin.stats.tasks": "Задачі (активні)",
    "admin.stats.task_lists": "Списки задач (активні)",
    "admin.stats.calendar_events": "Події календаря",
    "admin.stats.habits": "Звички (активні)",
    "admin.stats.recipes": "Рецепти",
    "admin.stats.shopping_lists": "Списки покупок (активні)",
    "admin.stats.shopping_items": "Товари (активні)",
    "admin.stats.tags": "Теги",
    "admin.stats.attachments": "Вкладення",
    "admin.stats.connections": "Зʼєднання",
    "admin.stats.storage_bytes": "Використано сховища",
    "admin.stats.llm_tokens": "LLM-токени (вх / вих)",
    "admin.stats.llm_calls": "LLM-виклики",
    "admin.stats.llm_cost": "Орієнтовна вартість LLM",
    "admin.stats.signup": "Зареєстровано",
    "admin.stats.last_seen": "Остання активність",
    "admin.stats.last_action": "Остання дія",
    "admin.actions.deactivate": "Деактивувати",
    "admin.actions.reactivate": "Реактивувати",
    "admin.actions.change_role": "Змінити роль",
    "admin.confirm.deactivate": "Деактивувати акаунт",
    "admin.confirm.reactivate":
      "Реактивувати цей акаунт? Користувач знову зможе входити.",
    "admin.confirm.changeRole": "Застосувати нову роль",
    "admin.deactivate.reason_label": "Причина",
    "admin.deactivate.reason_required":
      "Обовʼязково — щонайменше 3 символи. Записується до журналу аудиту.",
    "admin.audit.title": "Адмін · Журнал аудиту",
    "admin.audit.empty": "Записів немає.",
    "admin.audit.filter.action": "Дія",
    "admin.audit.columns.when": "Коли",
    "admin.audit.columns.action": "Дія",
    "admin.audit.columns.actor": "Хто",
    "admin.audit.columns.target": "Над ким",
    "admin.audit.columns.reason": "Причина",
    "admin.audit.actions.user_deactivate": "Деактивовано користувача",
    "admin.audit.actions.user_reactivate": "Реактивовано користувача",
    "admin.audit.actions.user_role_change": "Змінено роль користувача",
    "admin.audit.actions.bootstrap_promote": "Bootstrap-промоушн",
    "admin.pagination.prev": "Назад",
    "admin.pagination.next": "Далі",
    "admin.pagination.pageOf": "Сторінка {page} з {total}",
    "admin.errors.lastSuperadmin":
      "Не можна понизити останнього суперадміна. Спочатку підвищіть інший акаунт.",
    "admin.errors.cannotSelfDeactivate":
      "Не можна деактивувати власний акаунт.",
    "admin.errors.alreadyDeactivated": "Цей акаунт уже деактивовано.",
    "admin.errors.notDeactivated": "Цей акаунт не деактивовано.",
    "admin.errors.noopRoleChange": "Користувач уже має цю роль.",
    "admin.errors.userNotFound": "Користувача не знайдено.",
};

const STRINGS: Record<LangId, Partial<Record<keyof typeof EN_STRINGS, string>>> = {
  en: EN_STRINGS,
  uk: UK_STRINGS,
};

interface LanguageContextValue {
  lang: LangId;
  setLang(l: LangId): void;
  langs: typeof LANGS;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

interface LanguageProviderProps {
  children: ReactNode;
  storage?: Storage;
  initial?: LangId;
}

export function LanguageProvider({
  children,
  storage,
  initial,
}: LanguageProviderProps): JSX.Element {
  const store =
    storage ??
    (typeof globalThis.localStorage !== "undefined"
      ? globalThis.localStorage
      : undefined);

  const [lang, setLangState] = useState<LangId>(() => {
    if (initial !== undefined) return initial;
    if (store === undefined) return DEFAULT_LANG;
    const raw = store.getItem(STORAGE_KEY);
    return isLangId(raw) ? raw : DEFAULT_LANG;
  });

  const setLang = useCallback(
    (l: LangId) => {
      setLangState(l);
      store?.setItem(STORAGE_KEY, l);
    },
    [store],
  );

  useEffect(() => {
    if (storage !== undefined) return;
    if (typeof window === "undefined") return;
    const handler = (event: StorageEvent): void => {
      if (event.key === STORAGE_KEY && isLangId(event.newValue)) {
        setLangState(event.newValue);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storage]);

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, setLang, langs: LANGS }),
    [lang, setLang],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used inside <LanguageProvider>");
  }
  return ctx;
}

export type TVars = Record<string, string | number>;
export type TFn = (key: string, vars?: TVars) => string;
export type TnFn = (key: string, count: number, vars?: TVars) => string;

function lookup(lang: LangId, key: string): string | undefined {
  const dict = STRINGS[lang] ?? STRINGS.en;
  const localized = (dict as Record<string, string | undefined>)[key];
  if (localized !== undefined) return localized;
  return (STRINGS.en as Record<string, string | undefined>)[key];
}

function interp(s: string, vars: TVars | undefined): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k: string) =>
    vars[k] !== undefined ? String(vars[k]) : `{${k}}`,
  );
}

/**
 * Translation hook. Returns ``[lang, T, Tn]``.
 *
 * - ``T(key, vars?)`` — current-language string with ``{name}`` slots
 *   substituted from ``vars``. Falls back to English, then the key.
 * - ``Tn(key, count, vars?)`` — pluralised lookup. Resolves
 *   ``${key}.${rule}`` from ``Intl.PluralRules``, falling back to
 *   ``${key}.other``. ``count`` is auto-merged into ``vars``.
 */
export function useT(): readonly [LangId, TFn, TnFn] {
  const { lang } = useLanguage();

  const T = useCallback<TFn>(
    (key, vars) => interp(lookup(lang, key) ?? key, vars),
    [lang],
  );

  const Tn = useCallback<TnFn>(
    (key, count, vars) => {
      const rule = new Intl.PluralRules(lang === "uk" ? "uk" : "en").select(
        count,
      );
      const merged: TVars = { count, ...(vars ?? {}) };
      const variant = lookup(lang, `${key}.${rule}`);
      const fallback = lookup(lang, `${key}.other`);
      return interp(variant ?? fallback ?? key, merged);
    },
    [lang],
  );

  return [lang, T, Tn] as const;
}

function isLangId(v: unknown): v is LangId {
  return typeof v === "string" && LANGS.some((l) => l.id === v);
}

// ---- Legacy export ------------------------------------------------------
//
// The old ``t`` object is kept so existing components (LoginScreen,
// NotesScreen, etc.) keep compiling during the design rebuild. New
// components use ``const [, T] = useT(); T("signIn")``. The PR-by-PR
// migration sweeps each existing component over.
//
// Re-exports the ``as const`` literal so each ``t.signIn`` is the
// precise string type — ``noUncheckedIndexedAccess`` doesn't widen.

export const t = EN_STRINGS;
